import { DestroyRef, inject, Injectable } from '@angular/core';
import { combineLatest, of, shareReplay } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { InteractionService, isDefined, RestaurantAppService } from '@core';
import { ConfigSchema } from '@app/schemas/config';
import { FileCacheRepository } from '@core/lib/repositories/file-cache.repository';
import { config as defaultConfig } from '../../config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly interactionService = inject(InteractionService);
  private readonly RestaurantAppService = inject(RestaurantAppService);
  private readonly fileCacheRepository = inject(FileCacheRepository);

  public readonly config$ = this.RestaurantAppService.app$.pipe(
    map((app) => {
      return (
        app?.instance?.config || defaultConfig
      ) as ConfigSchema;
    }),
    shareReplay(1),
  );

  public readonly files$ = this.RestaurantAppService.app$.pipe(
    map((app) => {
      return app?.instance?.files || [];
    }),
    switchMap((files) => files.length === 0 ? of([]) : combineLatest(
      files.map(({ url }) => this.fileCacheRepository.one$(url)),
    ).pipe(
      map((fileCaches) => {
        return files.map((file, index) => {
          const fileCache = fileCaches[index];

          return {
            ...file,
            localUrl: fileCache ? URL.createObjectURL(fileCache.blob) : undefined,
          };
        });
      }),
    )),
    shareReplay(1),
  );

  public initialize(): void {
    this.config$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ idleDelay }) => {
      this.interactionService.interact();
      this.interactionService.idleDelay = isDefined(idleDelay) ? idleDelay : 'default';
    });
  }

}
